import {
  mapCreativeNodeDto,
  mapGraphAggregateResponseDto,
  mapImportTemplateDemoResponseDto,
  mapNodeEdgeDto,
  mapNodeValidationResponseDto,
  sanitizePatchForOcc,
  unwrapEdgeEnvelope,
  unwrapNodeEnvelope,
  type CreateBoundOutputResponseDto,
  type CreateCreativeNodeRequestDto,
  type CreateNodeEdgeRequestDto,
  type CreativeNode,
  type CreativeNodeDto,
  type CreativeNodeResponseDto,
  type GraphAggregate,
  type GraphAggregateResponseDto,
  type ImportTemplateDemoRequestDto,
  type ImportTemplateDemoResponseDto,
  type NodeEdge,
  type NodeEdgeDto,
  type NodeEdgeResponseDto,
  type NodeValidationResponseDto,
  type NodeValidationResult,
  type TemplateDemoImportResult,
} from '@/types';

import type { DagService } from '../dagService';
import { httpRequestWithRefresh, type HttpClientConfig } from './httpApiClient';
import { unwrapResult } from './httpEnvelope';

/**
 * HTTP impl of :type:`DagService`(Beta v0.0.1 per-doc OCC)
 * -----------------------------------------------------------------------------
 * 對應 docs/40 §3.6 / §3.7 / §4.2 endpoint。`X-Current-Group` header 自動注入
 * (httpRequest 內 — `/api/projects/*` 不在 exempt)。
 */

const PATH = {
  graph: (pid: string) => `/api/projects/${encodeURIComponent(pid)}/graph`,
  nodes: (pid: string) => `/api/projects/${encodeURIComponent(pid)}/nodes`,
  node: (pid: string, nid: string) =>
    `/api/projects/${encodeURIComponent(pid)}/nodes/${encodeURIComponent(nid)}`,
  edges: (pid: string) => `/api/projects/${encodeURIComponent(pid)}/edges`,
  edge: (pid: string, eid: string) =>
    `/api/projects/${encodeURIComponent(pid)}/edges/${encodeURIComponent(eid)}`,
} as const;

export function createHttpDagService(config: HttpClientConfig): DagService {
  return {
    async getGraph(pid: string): Promise<GraphAggregate> {
      const env = await httpRequestWithRefresh<GraphAggregateResponseDto>(
        config,
        'GET',
        PATH.graph(pid),
      );
      return mapGraphAggregateResponseDto(unwrapResult(env, 'dag.getGraph'));
    },

    nodes: {
      async create(pid: string, req: CreateCreativeNodeRequestDto): Promise<CreativeNode> {
        const env = await httpRequestWithRefresh<CreativeNodeResponseDto | CreativeNodeDto>(
          config,
          'POST',
          PATH.nodes(pid),
          req,
        );
        return mapCreativeNodeDto(unwrapNodeEnvelope(unwrapResult(env, 'dag.node.create')));
      },

      async patch(
        pid: string,
        nid: string,
        patch: Record<string, unknown>,
        expectedVersion: number,
      ): Promise<CreativeNode> {
        // Client-side sanitize:移除 immutable field(CREATIVE_NODE_IMMUTABLE_FIELDS)預先防 BE 422 GRAPH_OPS_INVALID
        const sanitized = sanitizePatchForOcc(patch);
        const env = await httpRequestWithRefresh<CreativeNodeResponseDto | CreativeNodeDto>(
          config,
          'PATCH',
          PATH.node(pid, nid),
          { patch: sanitized, expected_version: expectedVersion },
        );
        return mapCreativeNodeDto(unwrapNodeEnvelope(unwrapResult(env, 'dag.node.patch')));
      },

      async delete(pid: string, nid: string): Promise<void> {
        await httpRequestWithRefresh(config, 'DELETE', PATH.node(pid, nid));
      },

      // R134(docs/75 §3)— 節點即時驗證(A/B 卡;純讀不扣點,與 submit 同判準)。
      async validation(pid: string, nid: string): Promise<NodeValidationResult> {
        const env = await httpRequestWithRefresh<NodeValidationResponseDto>(
          config,
          'GET',
          `${PATH.node(pid, nid)}/validation`,
        );
        return mapNodeValidationResponseDto(unwrapResult(env, 'dag.node.validation'));
      },

      async createBound(
        pid: string,
        req: CreateCreativeNodeRequestDto,
      ): Promise<{ node: CreativeNode; edge: NodeEdge }> {
        // R136(docs/76 §2.3A)— response 是 `{node, edge}` 雙鍵(server 同時建卡+綁定
        // +建邊),**不走**單鍵 unwrapNodeEnvelope。
        const env = await httpRequestWithRefresh<CreateBoundOutputResponseDto>(
          config,
          'POST',
          PATH.nodes(pid),
          req,
        );
        const body = unwrapResult(env, 'dag.node.createBound');
        return { node: mapCreativeNodeDto(body.node), edge: mapNodeEdgeDto(body.edge) };
      },

      // R208(docs/85 §6.2 / ADR-042)— 範本示範素材伺服端匯入(綁定由 server 完成;
      // 寫操作 → httpRequest 自動帶 CSRF + X-Current-Group,與其他 node 寫入同慣例)。
      async importTemplateDemo(
        pid: string,
        nid: string,
        req: ImportTemplateDemoRequestDto,
      ): Promise<TemplateDemoImportResult> {
        const env = await httpRequestWithRefresh<ImportTemplateDemoResponseDto>(
          config,
          'POST',
          `${PATH.node(pid, nid)}/import-template-demo`,
          req,
        );
        return mapImportTemplateDemoResponseDto(unwrapResult(env, 'dag.node.importTemplateDemo'));
      },
    },

    edges: {
      async create(pid: string, req: CreateNodeEdgeRequestDto): Promise<NodeEdge> {
        const env = await httpRequestWithRefresh<NodeEdgeResponseDto | NodeEdgeDto>(
          config,
          'POST',
          PATH.edges(pid),
          req,
        );
        return mapNodeEdgeDto(unwrapEdgeEnvelope(unwrapResult(env, 'dag.edge.create')));
      },

      async delete(pid: string, eid: string): Promise<void> {
        await httpRequestWithRefresh(config, 'DELETE', PATH.edge(pid, eid));
      },
    },
  };
}
